import { useEffect, useMemo, useState } from "react";
import { getFragmentData } from "@/__generated__";
import {
  ClientServiceProfileFragmentFragmentDoc,
  PackageUnitTypeEnum,
} from "@/__generated__/graphql";
import { Divider } from "@/components/shared/divider";
import { ClipboardText, Cube, ListPlus, ThumbsUp } from "@/components/shared/icons";
import { ListHeading } from "@/components/shared/list-header";
import { ServiceRadioItem } from "@/components/shared/service-radio-item";
import { Typography } from "@/components/shared/typography";
import { Badge, RadioGroup, View } from "@/components/ui";
import {
  firstCatPkgCode,
  firstDogPkgCode,
  getPetGroomingAddOnPackageTitle,
  getPetPackages,
  PetAddOnCodes,
  PetGroomingTypes,
  PetShowerAddOnCode,
} from "@/constants/booking";
import { InlineServiceCard } from "@/containers/profile/service-profile/inline-service-card";
import { transformServiceProfileToDetail } from "@/containers/profile/service-profile/lib";
import { useClientServiceProfileByFiltersQuery } from "@/gqlhooks/profile/useClientServiceProfileByFiltersQuery";
import { formatPrice } from "@/lib/helpers/number";
import { useAuthState } from "@/store/auth";
import { useBookingState } from "@/store/booking/useBookingState";
import { AuthModals } from "@/types/users";

import { ServiceCreditTopUpCard } from "../components/service-credit-topup-card";
import { HelpCard } from "./help-card";
import { PetProfileModal } from "./pet-profile-modal";

export const PetGroomingBooking = () => {
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const {
    data: {
      packageDetails,
      packageDetailsFilter,
      selectedPackageIds,
      petProfileInfo,
      isEmptyErrorPetProfile,
    },
    setBookingState,
  } = useBookingState();
  const {
    showModal,
    data: { userInfo },
  } = useAuthState();

  const clientId = userInfo.id;

  const { data } = useClientServiceProfileByFiltersQuery("pet");
  const profiles = data?.clientServiceProfileByFilters.clientServiceProfiles ?? [];

  const isDogPet = useMemo(() => {
    return packageDetailsFilter?.unitTypes?.[0] === PackageUnitTypeEnum.Dog;
  }, [packageDetailsFilter?.unitTypes?.[0]]);

  const petPackages = useMemo(() => {
    return packageDetails.filter(
      (pkg) => pkg.unitType === (isDogPet ? PackageUnitTypeEnum.Dog : PackageUnitTypeEnum.Cat),
    );
  }, [packageDetails, isDogPet]);

  const addOnPackage = packageDetails
    ?.filter((pkg) => PetAddOnCodes.includes(pkg.code))
    .sort((a, b) => {
      if (a.code === PetShowerAddOnCode) return 1;
      if (b.code === PetShowerAddOnCode) return -1;
      return 0;
    });

  const isExpressGroomingSelected = useMemo(() => {
    return petPackages.some(
      (pkg) => selectedPackageIds.includes(pkg.id) && pkg.description.includes("Express"),
    );
  }, [petPackages, selectedPackageIds]);

  useEffect(() => {
    if (petPackages) {
      const firstPkg = petPackages.find((pkg) =>
        [firstDogPkgCode, firstCatPkgCode].includes(pkg.code),
      );
      if (firstPkg)
        setBookingState({
          selectedPackageIds: [firstPkg.id],
        });
    }
  }, [petPackages]);

  useEffect(() => {
    if (profiles.length > 0 && isDogPet !== undefined) {
      const targetPetType = isDogPet ? "dog" : "cat";
      const profilesWithFragments = profiles.map((profile) => ({
        fragment: getFragmentData(ClientServiceProfileFragmentFragmentDoc, profile),
      }));
      const profilesOfType = profilesWithFragments.filter(({ fragment }) => {
        const petType = (fragment.profileAttributes as { type?: string })?.type;
        return petType === targetPetType;
      });

      if (profilesOfType.length > 0) {
        let selected = profilesOfType.find(({ fragment }) => fragment.primary);

        if (!selected) {
          selected = profilesOfType[0];
        }

        if (selected) {
          setBookingState({
            petProfileInfo: {
              id: selected.fragment.id,
              ...transformServiceProfileToDetail("pet", selected.fragment),
            },
            selectedServiceProfileId: selected.fragment.id,
          });
        }
      }
    }
  }, [profiles, isDogPet]);

  const handleChangeProfile = () => {
    if (!clientId) {
      showModal(AuthModals.LOGIN);
      return;
    }

    setOpenProfileModal(true);
  };

  return (
    <View className="mt-2 flex flex-col gap-4">
      <View className="mx-4">
        <ServiceCreditTopUpCard service="pet" />
      </View>

      <Divider className="border-b-2" />

      <View className="mx-4 flex flex-col gap-2">
        <View className="flex flex-row gap-2">
          <ListHeading title="Pet Profile" icon={ClipboardText} />
          <Badge variant="success" size="sm">
            Required
          </Badge>
        </View>
        <Typography color="foreground-intermediate" variant="body-sm">
          Share a few details so our groomers can prep the perfect session for your furry friend.
        </Typography>

        {profiles.length && petProfileInfo ? (
          <InlineServiceCard
            type="pet"
            profile={petProfileInfo}
            onChange={() => setOpenProfileModal(true)}
          />
        ) : (
          <InlineServiceCard
            type="add"
            label="Tell us about your pets"
            onChange={handleChangeProfile}
          />
        )}
        {isEmptyErrorPetProfile && (
          <Typography variant="caption-md" color="danger" className="italic">
            To proceed, please provide the required details.
          </Typography>
        )}
      </View>

      <Divider className="h-0.5px" />

      <View className="mx-4 flex flex-col gap-2">
        <ListHeading title="Select Package" icon={Cube} />
        <View className="flex flex-col gap-4">
          {PetGroomingTypes.map((item) => {
            return (
              <View key={item.title}>
                <View className="mb-[2px] flex flex-row items-center justify-between">
                  <Typography variant="body-sm">{item.title}</Typography>
                  {item.recommended && <RecommendedBadge />}
                </View>
                <Typography className="pb-4" variant="caption-md" color="foreground-intermediate">
                  {item.description}
                </Typography>
                <View>
                  <RadioGroup value="true" onValueChange={() => {}}>
                    {petPackages
                      .filter((pkg) => pkg.description.includes(item.keyword))
                      .sort((a, b) => a.unitValue - b.unitValue)
                      .map((pkg) => {
                        const pkgInfo = getPetPackages(pkg.code);
                        if (pkgInfo)
                          return (
                            <ServiceRadioItem
                              showSparkle
                              key={pkg.code}
                              title={pkgInfo.title}
                              subTitle={formatPrice(pkg.unitValue)}
                              value={String(selectedPackageIds.includes(pkg.id))}
                              selected={selectedPackageIds.includes(pkg.id)}
                              descRender={
                                pkgInfo.description && (
                                  <Typography
                                    className="ml-5"
                                    variant="body-md"
                                    color="foreground-intermediate"
                                  >
                                    {pkgInfo.description}
                                  </Typography>
                                )
                              }
                              onSelect={() =>
                                setBookingState({
                                  selectedPackageIds: [pkg.id],
                                })
                              }
                            />
                          );
                      })}
                  </RadioGroup>
                </View>
              </View>
            );
          })}
        </View>
      </View>

      <Divider className="my-2 border-b-2" />

      <View className="mx-4 flex flex-col gap-2">
        <View className="flex flex-row gap-2">
          <ListHeading title="Add-On" icon={ListPlus} />
          <Badge variant="neutral" size="sm" outline={false}>
            Optional
          </Badge>
        </View>
        <Typography color="foreground-intermediate" variant="body-sm">
          Enhance your pet's care
        </Typography>
        {addOnPackage.map((pkg) => {
          return (
            <RadioGroup value={"true"} onValueChange={() => {}}>
              <ServiceRadioItem
                showSparkle
                disabled={pkg.code === PetShowerAddOnCode && !isExpressGroomingSelected}
                title={getPetGroomingAddOnPackageTitle(pkg.code)}
                subTitle={formatPrice(pkg.unitValue)}
                value={String(selectedPackageIds.includes(pkg.id))}
                selected={selectedPackageIds.includes(pkg.id)}
                onSelect={() =>
                  setBookingState({
                    selectedPackageIds: selectedPackageIds.includes(pkg.id)
                      ? selectedPackageIds.filter((id) => id !== pkg.id)
                      : [...selectedPackageIds, pkg.id],
                  })
                }
              />
            </RadioGroup>
          );
        })}
      </View>

      <Divider />

      <HelpCard name="pet-grooming" />

      <Divider />

      <PetProfileModal open={openProfileModal} setOpen={setOpenProfileModal} />

      <Divider className="my-2 mb-6 border-b-2 md:hidden" />
    </View>
  );
};

const RecommendedBadge = () => {
  return (
    <Badge variant="success">
      <View className="flex flex-row items-center gap-1">
        <ThumbsUp weight="fill" className="text-success-on-surface size-4" size={16} />
        <Typography variant="body-sm" color="success-on-surface">
          Recommended
        </Typography>
      </View>
    </Badge>
  );
};
